<template>
    <page-layout :readonly="isBriefAccepted && !isAdditionalOrder" :hide-footer="isBriefAccepted && !isAdditionalOrder">
        <template #header>
            <step-header
                :current-step="stepNumber"
                :title="pageTitle"
                :total-steps="totalSteps"
                :is-loading="isLoading"
                :use-exit-modal="!isBriefAccepted && exitConfirmationRequired"
                :skip-steps="isBriefAccepted"
                :is-preview="isPreview"
            />
        </template>
        <template #default>
            <g-loader v-if="isLoading" type="fixed" />
            <template v-if="brief && !isLoading">
                <g-loader v-if="isLoadingProducts" type="fixed" />
                <template v-else>
                    <error-message-card class="g-mb-5" :errors="errors" />
                    <step-error v-if="step && step.error" class="margin-bottom-normal" :step="step" />

                    <g-typography
                        v-if="!isBriefAccepted"
                        tag="p"
                        :value="pageDescription"
                        style="margin-bottom: 15px"
                    />

                    <out-of-stock-brief v-if="preselectedOutOfStock" :brief="brief" />
                    <products-website
                        v-if="selectionMethodWebsite || selectionMethodWebsiteShopifySharedCart"
                        :brief="brief"
                        @selected="initFinishedProducts" />
                    <products-website-shopify
                        v-else-if="selectionMethodWebsiteShopify"
                        :brief="brief"
                        :product-limit-reached="productFromUrlLimitReached"
                        @selected="initFinishedProductsFromUrl"
                    />

                    <product-stats
                        v-if="addProductVisibility || isAdditionalOrder"
                        :selection="finishedProducts"
                        :brief="brief"
                    />
                    <product-shipping-info
                        v-if="!addProductVisibility && !isAdditionalOrder"
                        :order="findExistingOrder"
                        :brief="brief"
                    />

                    <div class="g-mb-5">
                        <activation-brief-products-item
                            v-for="(product, idx) in finishedProducts"
                            :key="idx + product.id"
                            :brief="brief"
                            :product="readStore(product.id)"
                            :options="product.options"
                            :has-existing-order="hasExistingOrder"
                            :is-additional-order="isAdditionalOrder"
                            @edit="onEdit(product, idx)"
                            @remove="onRemove(idx)"
                        />
                    </div>
                    <template v-if="limitationMethodSlots">
                        <add-product
                            v-for="(slot, idx) in availableSlotItems"
                            :key="'slot.' + idx"
                            class="g-mb-5"
                            @click.native="addSlotProduct(slot, idx)"
                        />
                    </template>
                    <template v-else>
                        <add-product v-if="addProductVisibility" @click.native="openPanel" />
                    </template>
                    <template v-if="selectionMethodUnlimited">
                        <br />
                        <g-typography
                            size="small"
                            color="subtitle"
                            tag="p"
                            value="You may choose as many products as you'd like"
                        />
                    </template>
                    <activation-brief-products-panel
                        :visibility="isPanelVisible"
                        :editing-product="readStore(editingProduct && editingProduct.id)"
                        :current-selection="editingProduct"
                        :panel-slot="panelSlot"
                        :brief="brief"
                        @close="closePanel"
                        @added="onProductAdd"
                        @updated="onProductUpdate"
                    />
                </template>
            </template>
        </template>
        <template #footer>
            <step-footer
                v-if="isBriefEditable"
                :is-next-disabled="false"
                :loading="isCompletingBrief"
                :current-step="stepNumber"
                :step-completed="isBriefCompleted && !isAdditionalOrder"
                :total-steps="totalSteps"
                :use-exit-modal="!isBriefAccepted && exitConfirmationRequired"
                :is-preview="isPreview"
                @go-next-step="goNextStep"
                @go-previous-step="goPreviousStep"
            />

            <g-button
                v-if="isAdditionalOrder"
                :loading="isCompletingBrief"
                size="xlarge"
                shape="round"
                theme="live"
                type="primary"
                class="g-w-full"
                @click="goNextStep"
            >
                Continue
            </g-button>
        </template>
    </page-layout>
</template>

<script>
    import { get, zipWith } from 'lodash';
    import ActivationBriefMixin from './mixins/ActivationBriefMixin';
    import ActivationBriefProductConfigMixin from './mixins/ActivationBriefProductConfigMixin';
    import PageLayout from '../../layouts/PageLayout';
    import StepHeader from './partials/StepHeader.vue';
    import ActivationBriefProductsItem from './partials/products/ActivationBriefProductsItem.vue';
    import AddProduct from './partials/products/AddProduct.vue';
    import StepFooter from './partials/StepFooter.vue';
    import { getCustomizableText, getCustomizableTitle } from '../../../helpers/activations/default-texts';
    import ProductsWebsite from './partials/products/ProductsWebsite.vue';
    import ProductsWebsiteShopify from './partials/products/ProductsWebsiteShopify.vue';
    import ProductStats from './partials/products/ProductStats.vue';
    import ProductShippingInfo from './partials/products/ProductShippingInfo.vue';
    import ActivationBriefProductsPanel from './partials/products/ActivationBriefProductsPanel.vue';
    import ExitConfirmationMixin from './mixins/ExitConfirmationMixin';
    import ErrorMessageCard from './partials/ErrorMessageCard';
    import StepError from './partials/StepError';
    import ActivationBriefProductsMixin from '../../providers/mixins/ActivationBriefProductsMixin';
    import ActivationBriefOrderMixin from './mixins/ActivationBriefOrderMixin';
    import OutOfStockBrief from './partials/products/OutOfStockBrief';

    export default {
        name: 'ActivationBriefProductsView',
        components: {
            PageLayout,
            StepHeader,
            StepFooter,
            StepError,
            ActivationBriefProductsItem,
            AddProduct,
            ProductsWebsite,
            ProductsWebsiteShopify,
            ProductStats,
            ProductShippingInfo,
            ActivationBriefProductsPanel,
            ErrorMessageCard,
            OutOfStockBrief
        },
        mixins: [
            ActivationBriefMixin,
            ActivationBriefProductConfigMixin,
            ActivationBriefProductsMixin,
            ActivationBriefOrderMixin,
            ExitConfirmationMixin
        ],
        props: {
            step: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isCompletingBrief: {
                type: Boolean,
                default: false
            },
            errors: {
                type: Object | Array,
                default: () => ({})
            },
            errorMessage: {
                type: String,
                default: ''
            },
            isAdditionalOrder: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                isPanelVisible: false,
                editingProduct: null,
                editingIndex: null,
                finishedProducts: [],
                slotItems: [],
                selectedSlotItems: [],
                panelSlot: null,
                panelSlotIdx: null
            };
        },
        created() {
            if (this.limitationMethodSlots) {
                this.slotItems = this.productConfig.items;
            }

            if (this.brief && this.hasExistingOrder) {
                this.handleExisting();
            } else if (
                this.brief &&
                this.selectionMethodPreselected &&
                get(this.findExistingOrder, 'items.length') !== get(this.productConfig, 'items.length')
            ) {
                this.populateOrderItemsWithPreselected();
            }
        },
        computed: {
            availableSlotItems() {
                return this.slotItems.filter((slot) => !slot.chosen);
            },
            addProductVisibility() {
                if (this.selectionMethodWebsite || this.selectionMethodWebsiteShopify) {
                    return false;
                }

                if (this.canAddAdditionalOrders) {
                    return this.selectionMethodUnlimited
                        ? true
                        : this.finishedProducts.length < this.productConfig.quantity_limit;
                }

                if (!this.isBriefEditable) {
                    return false;
                }

                if (this.selectionMethodUnlimited) {
                    return true;
                } else if (this.selectionMethodLimitAmount) {
                    return this.finishedProducts.length < this.productConfig.quantity_limit;
                }

                return false;
            },
            pageTitle() {
                return getCustomizableTitle(this.brief, 'products_title');
            },
            pageDescription() {
                return getCustomizableText(this.brief, 'products_description', this.getConfigText);
            },
            stepNumber() {
                return get(this, 'step.meta.stepNumber', 0);
            },
            totalSteps() {
                return get(this, 'step.meta.totalSteps', 0);
            },
            preselectedOutOfStock() {
                return this.selectionMethodPreselected && this.finishedProducts.length === 0;
            },
            productFromUrlLimitReached() {
                return this.finishedProducts?.length >= this.productConfig?.quantity_limit;
            }
        },
        methods: {
            /**
             * Is used to handle loading products that have previously been selected, but the brief is only partially complete and hasn't been submitted.
             */
            handleExisting() {
                if (this.canAddAdditionalOrders) {
                    return;
                }

                const productOptionValues = this.mapOrderItems('option_values').map(this.transformOrderOptionValues);
                const productSlotIds = this.mapOrderItems('slot_id');

                this.getProducts(this.mapOrderItems('product_id')).then((products) => {
                    this.initFinishedProducts(products, productOptionValues, productSlotIds, true);
                });
            },
            /**
             * Assumes that products and optionValues are in same order/length
             * @param products
             * @param optionValues
             * @param slotIds
             */
            initFinishedProducts(products, optionValues, slotIds = [], existing = false, fromProductUrl = false) {
                if (!fromProductUrl && products.length !== optionValues.length) {
                    throw new Error('Products and values are not the same length');
                }

                this.finishedProducts = zipWith(products, optionValues, slotIds, (product, optionValue, slotId) => {
                    const productConfig = this.getProductConfigItem('product_id', product.id);

                    return {
                        id: product.id,
                        quantity: existing ? 1 : productConfig.quantity,
                        slot_id: slotId,
                        options: optionValue
                    };
                });

                if (this.limitationMethodSlots) {
                    this.finishedProducts.forEach((product) => {
                        this.slotItems.forEach((slotItem) => {
                            if (slotItem.id === product.slot_id) {
                                this.selectedSlotItems.push(slotItem);
                                this.$set(slotItem, 'chosen', true);
                            }
                        });
                    });
                }
            },
            initFinishedProductsFromUrl(product, optionValue) {
                if (product.has_options) {
                    this.onEdit(product, this.finishedProducts?.length);
                } else {
                    this.finishedProducts.push({
                        id: product.id,
                        quantity: 1,
                        slot_id: null,
                        options: optionValue
                    });
                }
            },
            /**
             * @param optionValues
             * @returns {*}
             */
            transformOrderOptionValues(optionValues) {
                return optionValues.map((optionValue) => {
                    return {
                        id: optionValue.option_id,
                        name: optionValue.name || optionValue.label,
                        value_id: optionValue.id || optionValue.value
                    };
                });
            },
            /**
             * @param optionValues
             * @returns {*}
             */
            transformProductConfigOptionValues(optionValues) {
                return optionValues.map((optionValue) => {
                    return {
                        id: optionValue.option_id,
                        name: optionValue.label,
                        value_id: optionValue.value
                    };
                });
            },
            /**
             * @param slot
             * @param idx
             */
            addSlotProduct(slot, idx) {
                this.panelSlot = slot;
                this.panelSlotIdx = idx;
                this.openPanel();
            },
            openPanel() {
                this.isPanelVisible = true;
            },
            closePanel() {
                this.editingProduct = null;
                this.isPanelVisible = false;
            },
            /**
             * @param product
             * @param idx
             */
            onEdit(product, idx) {
                this.editingProduct = product;
                this.editingIndex = idx;
                this.openPanel();
            },
            /**
             * @param idx
             */
            onRemove(idx) {
                if (this.limitationMethodSlots) {
                    const product = this.finishedProducts[idx];
                    const slotItemIndex = this.selectedSlotItems.findIndex((item) => {
                        return item.id === product.slot_id;
                    });

                    if (slotItemIndex > -1) {
                        const slotItem = this.selectedSlotItems.find((item) => {
                            return item.id === product.slot_id;
                        });
                        this.$set(slotItem, 'chosen', false);
                        this.$delete(this.selectedSlotItems, slotItemIndex);
                    }
                }

                this.$delete(this.finishedProducts, idx);
            },
            /**
             * @param data
             */
            onProductUpdate(data) {
                const current = this.finishedProducts[this.editingIndex] ?? {};
                this.$set(this.finishedProducts, this.editingIndex, {
                    ...current,
                    ...data.payload
                });
                this.closePanel();
            },
            /**
             * @param data
             */
            onProductAdd(data) {
                let slotId = null;

                if (this.limitationMethodSlots) {
                    const slotItem = this.availableSlotItems[this.panelSlotIdx];

                    if (slotItem) {
                        slotId = slotItem.id;
                        this.selectedSlotItems.push(slotItem);
                        this.$set(slotItem, 'chosen', true);
                    }
                }

                this.writeStore(data.product);
                this.finishedProducts.push({
                    ...data.payload,
                    slot_id: slotId
                });

                this.closePanel();
            },
            goNextStep() {
                let step = this.step;
                if (this.isAdditionalOrder) {
                    step = {
                        error: null,
                        is_complete: false,
                        is_started: false,
                        is_valid: true,
                        meta: {
                            stepNumber: 1,
                            totalSteps: 2
                        },
                        name: 'products'
                    };
                }

                this.$emit('go-next-step', step, {
                    products: this.finishedProducts,
                    additional: this.isAdditionalOrder
                });
            },
            goPreviousStep() {
                this.$emit('go-previous-step', this.step, { products: this.finishedProducts });
            },
            populateOrderItemsWithPreselected() {
                this.getProducts(this.mapProductConfigItems('product_id')).then((products) => {
                    this.initFinishedProducts(
                        products,
                        this.mapProductConfigItemValues.map(this.transformProductConfigOptionValues)
                    );
                });
            }
        }
    };
</script>
